/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { provider, section, theme } from '../../config.json'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { accountAPI } from 'api/account'
import { appAPI } from 'api/app'

import { Session, Settings } from 'models/app'
import { User, Account, AdditionalUserList } from 'models/account'
import { TwoFactorMethod, TwoFactorMethodList } from 'models/authentication'
import { mapToAccount } from 'helpers/utils'

/**********************************************************************************************************
 *    SLICE
 **********************************************************************************************************/
interface Router {
    template: 'loading' | 'user' | 'guest',
    meta: {
        loading: boolean,
        message: string | undefined
    }
}

interface AppState {
    appSession: Session,
    appSettings: Settings,
    appAuthentication: {
        additionalUsers: AdditionalUserList | undefined,
        twofactor: {
            primary: TwoFactorMethod | undefined,
            backup: TwoFactorMethodList | undefined
        }
    }
    appActiveRouter: Router
}

const initialState: AppState = {
    appSession: {
        account: {
            account_holder: '',
            account_number: null,
            company_name: '',
            id: null,
            type: 'personal',
            expiry: '',
            is_primary: false
        },
        authenticated: false,
        two_factor_required: false,
        user: null
    },
    appAuthentication: {
        additionalUsers: undefined,
        twofactor: {
            primary: undefined,
            backup: undefined
        }
    },
    appSettings: {
        section,
        provider,
        theme
    },
    appActiveRouter: {
        template: 'loading',
        meta: {
            loading: false,
            message: 'Initialising...'
        }
    }
}

export const applicationSlice = createSlice({
    name: 'application',
    initialState,
    reducers: {
        setAppSettings: (state, action: PayloadAction<Settings>) => {
            state.appSettings = action.payload
        },
        setUser: (state, action: PayloadAction<User>) => {
            state.appSession = {
                ...state.appSession,
                user: action.payload
            }
        },
        setAccount: (state, action: PayloadAction<Account>) => {
            state.appSession = {
                ...state.appSession,
                account: action.payload
            }
        },
        setAppActiveRouter: (state, action: PayloadAction<Router>) => {
            state.appActiveRouter = action.payload
        }
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                appAPI.endpoints.initialiseApplication.matchFulfilled,
                (state, { payload }) => {
                    const settings = payload?.settings
                    const session = payload?.session
                    const { authenticated, two_factor_required, user, current_account } = session

                    state.appSettings = {
                        ...state.appSettings,
                        ...settings,
                        theme: {
                            ...theme,
                            ...settings.theme
                        }
                    }

                    state.appSession = {
                        authenticated,
                        two_factor_required,
                        user,
                        account: current_account ? mapToAccount(current_account) : null
                    }

                    switch (true) {
                        case authenticated &&
                            !two_factor_required &&
                            typeof user === 'object' && user !== null &&
                            typeof current_account === 'object' && current_account !== null:

                            state.appActiveRouter = {
                                template: 'user',
                                meta: {
                                    loading: false,
                                    message: ''
                                }
                            }
                            break

                        default:

                            state.appActiveRouter = {
                                template: 'guest',
                                meta: {
                                    loading: false,
                                    message: ''
                                }
                            }
                            break
                    }
                }
            )

            /**********************************************************************************************************
             *   LOGIN
             **********************************************************************************************************/
            .addMatcher(
                appAPI.endpoints.login.matchFulfilled,
                (state, { payload }) => {
                    const session = payload?.session
                    const two_factor_list = payload?.two_factor
                    const authorised_accounts = payload?.authorised_accounts

                    state.appSession = session

                    if (authorised_accounts || two_factor_list) {
                        state.appAuthentication = {
                            ...state.appAuthentication,
                            additionalUsers: authorised_accounts,
                            twofactor: {
                                primary: two_factor_list && two_factor_list.filter((item: TwoFactorMethod) => item.is_primary)[0],
                                backup: two_factor_list
                            }
                        }
                    } else {
                        state.appActiveRouter = {
                            template: 'user',
                            meta: {
                                loading: false,
                                message: ''
                            }
                        }
                    }
                }
            )

            /**********************************************************************************************************
             *   LOGOUT
             **********************************************************************************************************/
            .addMatcher(
                appAPI.endpoints.logout.matchPending,
                (state) => {
                    state.appActiveRouter = {
                        template: 'loading',
                        meta: {
                            loading: false,
                            message: 'logging out...'
                        }
                    }
                }
            )
            .addMatcher(
                appAPI.endpoints.logout.matchFulfilled,
                (state) => {
                    state.appSettings = {
                        section: section,
                        provider: provider,
                        theme: theme
                    }
                }
            )

            /**********************************************************************************************************
             *   TWI FACTOR
             **********************************************************************************************************/
            .addMatcher(
                appAPI.endpoints.requestTwoFactorCode.matchPending,
                (state) => {
                    state.appActiveRouter = {
                        ...state.appActiveRouter,
                        meta: {
                            loading: true,
                            message: 'Requesting Two Factor...'
                        }
                    }
                }
            )
            .addMatcher(
                appAPI.endpoints.requestTwoFactorCode.matchFulfilled,
                (state, { payload }) => {
                    const twoFactorList = payload
                    const primary = twoFactorList.filter((item: TwoFactorMethod) => item.is_primary)[0]

                    state.appAuthentication = {
                        ...state.appAuthentication,
                        twofactor: {
                            primary,
                            backup: twoFactorList
                        }
                    }

                    state.appActiveRouter = {
                        ...state.appActiveRouter,
                        meta: {
                            loading: false,
                            message: ''
                        }
                    }
                }
            )

            .addMatcher(
                appAPI.endpoints.verifyTwoFactor.matchFulfilled,
                (state, { payload }) => {
                    if (payload?.session) {
                        state.appSession = payload.session
                        state.appActiveRouter = {
                            template: 'user',
                            meta: {
                                loading: false,
                                message: ''
                            }
                        }
                    }
                }
            )

            .addMatcher(
                appAPI.endpoints.selectMethod.matchFulfilled,
                (state, { payload }) => {
                    const methods: TwoFactorMethodList = payload?.methods
                    const type: string = payload?.type
                    const selectedAlternative: TwoFactorMethod = methods.filter((item: TwoFactorMethod) => type === item.type)[0]

                    state.appAuthentication = {
                        ...state.appAuthentication,
                        twofactor: {
                            ...state.appAuthentication.twofactor,
                            primary: selectedAlternative
                        }
                    }
                }
            )

            /**********************************************************************************************************
             *   AUTHORISED ACCOUNT
             **********************************************************************************************************/
            .addMatcher(
                appAPI.endpoints.getAuthorisedAccounts.matchPending,
                (state) => {
                    state.appActiveRouter = {
                        ...state.appActiveRouter,
                        meta: {
                            loading: true,
                            message: 'Fetching Accounts List...'
                        }
                    }
                }
            )
            .addMatcher(
                appAPI.endpoints.getAuthorisedAccounts.matchFulfilled,
                (state, { payload }) => {
                    state.appActiveRouter = {
                        ...state.appActiveRouter,
                        meta: {
                            loading: false,
                            message: ''
                        }
                    }

                    state.appAuthentication = {
                        ...state.appAuthentication,
                        additionalUsers: payload
                    }
                }
            )
            .addMatcher(
                appAPI.endpoints.selectUser.matchPending,
                (state) => {
                    state.appActiveRouter = {
                        ...state.appActiveRouter,
                        meta: {
                            loading: true,
                            message: 'Assigning Account & Finalising Login...'
                        }
                    }
                }
            )
            .addMatcher(
                appAPI.endpoints.selectUser.matchFulfilled,
                (state, { payload }) => {
                    const session = payload?.session
                    state.appSession = session
                    state.appActiveRouter = {
                        template: 'user',
                        meta: {
                            loading: false,
                            message: ''
                        }
                    }
                }
            )

            /**********************************************************************************************************
             *   CLOSE ACCOUNT
             **********************************************************************************************************/
            .addMatcher(
                accountAPI.endpoints.closeAccount.matchPending,
                (state) => {
                    state.appActiveRouter = {
                        template: 'loading',
                        meta: {
                            loading: false,
                            message: 'Closing account & logging out...'
                        }
                    }
                }
            )
            .addMatcher(
                accountAPI.endpoints.closeAccount.matchRejected,
                (state) => {
                    state.appActiveRouter = {
                        template: 'user',
                        meta: {
                            loading: false,
                            message: ''
                        }
                    }
                }
            )
            .addMatcher(
                accountAPI.endpoints.closeAccount.matchFulfilled,
                (state) => {
                    state.appActiveRouter = {
                        template: 'guest',
                        meta: {
                            loading: false,
                            message: ''
                        }
                    }
                }
            )
    }
})

export const {
    setAppSettings,
    setUser,
    setAccount,
    setAppActiveRouter
} = applicationSlice.actions

export default applicationSlice.reducer
